









































































































































import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { Options } from '@/models/options.model';
import { routeName } from '@/constants/route.constants';
import { PreferredContactMethodType } from '@/constants/preferred-contact-method-type';
import { PhoneNumberType } from '@/constants/phone-number-type';
import VueRecaptcha from 'vue-recaptcha';
import LoadingComponent from '@/components/shared/loading.component.vue';
import CustomerModule, {
  CUSTOMERS_MODULE_NAME
} from '@/store/modules/customer.module';
import RegisteredTagModule, {
  REGISTERED_TAG_MODULE_NAME
} from '@/store/modules/registered-tag.module';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import { mapState } from 'vuex';
import { Customer } from '@/models/DTO/customer/customer.model';
import { BaseAPIErrorModel } from '@/models/DTO/base-api-error.model';
import { CustomerBaseModel } from '@/models/DTO/customer/customer-base.model';
import CustomerShowFieldMixin from '@/mixins/customer-show-field.mixin';
import { RegisteredTag } from '@/models/DTO/registered-tag/registered-tag.model';
import { RegexConstants } from '@/constants/regex.constants';
import { PHONE_LINK } from '@/filters/phone.filter';
import { PUBLIC_EMAIL_DOMAIN } from '@/constants/anonymous-registration.constants';

const customerModule: CustomerModule = getModule(CustomerModule, store);

const registeredTagModule: RegisteredTagModule = getModule(
  RegisteredTagModule,
  store
);

@Component({
  components: {
    ValidationObserver,
    ValidationProvider,
    VueRecaptcha,
    LoadingComponent
  },
  computed: {
    ...mapState(REGISTERED_TAG_MODULE_NAME, {
      currentRegisteredTag: 'currentRegisteredTag',
      showLoading: 'showLoading',
      errorTagResponse: 'errorResponse'
    }),
    ...mapState(CUSTOMERS_MODULE_NAME, [
      'publicCustomer',
      'showButtonLoading',
      'errorResponse'
    ])
  }
})
export default class OwnerFormComponent extends mixins(CustomerShowFieldMixin) {
  @Prop({
    default: ''
  })
  public title: string;

  @Prop({
    required: true
  })
  public code: string;

  public currentRegisteredTag: RegisteredTag | null;
  public showLoading: boolean;
  public publicCustomer: Customer | null;
  public showButtonLoading: boolean;
  public errorResponse: BaseAPIErrorModel | null;
  public errorTagResponse: BaseAPIErrorModel | null;
  public firstName = '';
  public lastName = '';
  public email = '';
  public terms = true;
  public selectedPhoneType = '';
  public phone = '';
  public address1 = '';
  public address2 = '';
  public city = '';
  public state = '';
  public zipCode = '';
  public website = '';
  public disabledEmailLookup = true;
  public showEmailSearchLoading = false;
  public isMobilePhone = true;

  public get phoneFormatted(): string {
    return this.$options.filters?.phone(this.phone);
  }

  public get RegexConstants(): RegexConstants {
    return RegexConstants;
  }

  public methodOfContact: Array<Options> = Object.entries(
    PreferredContactMethodType
  ).map(([key, value]) => {
    return {
      text: key,
      value: value
    };
  });

  public phoneType: Array<Options> = Object.entries(PhoneNumberType).map(
    ([key, value]) => {
      return {
        text: key,
        value: value
      };
    }
  );

  public $refs: {
    customerForm: HTMLFormElement;
  };

  public get page(): string | null | undefined {
    return this.$route.name;
  }

  public get routeName(): typeof routeName {
    return routeName;
  }

  @Watch('selectedMethodOfContact')
  private onSelectedMethodOfContactChanged(newVal: string, oldVal: string) {
    if (newVal !== oldVal && oldVal !== '') {
      this.reset();
    }
  }

  @Watch('isMobilePhone')
  private onIsMobilePhoneChanged(newVal: boolean, oldVal: boolean) {
    if (newVal !== oldVal) {
      // Reset email on switch change.
      this.email = '';
    }
  }

  @Watch('errorResponse', { deep: true })
  private onErrorResponseChanged(value: BaseAPIErrorModel) {
    if (value) {
      const errors = value.getAllErrorMessages();
      return this.$toasted.error(errors.join());
    }
  }

  @Watch('errorTagResponse', { deep: true })
  private onErrorTagResponseChanged(value: BaseAPIErrorModel) {
    if (value) {
      const errors = value.getAllErrorMessages();
      return this.$toasted.error(errors.join());
    }
  }

  public async created() {
    if (this.page === routeName.public.editCustomer) {
      if (!this.currentRegisteredTag) {
        await registeredTagModule.getTagByCodeAction(this.code);
      }

      if (
        !this.errorTagResponse &&
        this.currentRegisteredTag &&
        this.currentRegisteredTag.customer
      ) {
        this.getCustomerModelForEditing(this.currentRegisteredTag.customer);
      }
    } else if (this.publicCustomer) {
      this.getCustomerModelForEditing(this.publicCustomer);
    }
  }

  public getCustomerModelForEditing(customer: Customer) {
    if (customer) {
      this.email = customer.email;
      this.firstName = customer.firstName || '';
      this.lastName = customer.lastName || '';
      this.selectedPhoneType = customer.phoneNumberTypeId?.toString() || '';
      this.selectedMethodOfContact =
        customer.preferredContactMethodId.toString() || '';
      this.phone = customer.phoneNumber || '';
      this.address1 = customer.address1 || '';
      this.address2 = customer.address2 || '';
      this.city = customer.city || '';
      this.state = customer.state || '';
      this.zipCode = customer.zipCode || '';
      this.website = customer.website || '';
    }
  }

  public reset() {
    // This only resets validation on the form elements
    // associated with PreferredContactMethodType
    this.$refs.customerForm.reset();
  }

  public setCustomerBaseModelForSaving(): CustomerBaseModel {
    // Set a variable to hold a unique phone email address. Use if email is blank or
    // it is a mobile number.
    const phoneEmail = `${Vue.filter(PHONE_LINK)(
      this.phone
    )}@${PUBLIC_EMAIL_DOMAIN}`;

    const model: CustomerBaseModel = {
      firstName: this.firstName || null,
      lastName: this.lastName || null,
      preferredContactMethodId: PreferredContactMethodType.Call, // Call will be a fallback
      email: this.email == '' ? phoneEmail : this.email
    };

    model.phoneNumber = this.phone || null;
    model.phoneNumberTypeId = this.isMobilePhone
      ? PhoneNumberType.Mobile
      : PhoneNumberType.Home;

    return model;
  }

  public async onSaveClick(): Promise<void> {
    const model = this.setCustomerBaseModelForSaving();

    if (this.currentRegisteredTag && this.currentRegisteredTag.customerId) {
      await customerModule.updateCustomerAction({
        id: this.currentRegisteredTag.customerId,
        ...model
      });

      if (!this.errorResponse) {
        this.$toasted.success('Owner updated successfully.');
      }
    }
  }

  public async onPayAndSaveClick(): Promise<void> {
    const model = this.setCustomerBaseModelForSaving();

    // store data for registration until after payment has been processed
    await customerModule.setPublicCustomerAction({ ...model });

    this.$router.replace({
      name: routeName.public.payment,
      params: {
        tagCode: this.code
      }
    });
  }
}
